import { Button, Checkbox, Flex, Typography } from 'antd'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from 'src/components'

import { groupScopes, type ScopeRow } from '../scopeModel'

const { Text } = Typography

type Props = {
  /** Catalog rows from Core (`useScopeCatalog`). */
  catalog: ScopeRow[]
  /** Currently-granted scope codes. */
  value: Set<string>
  onChange: (next: Set<string>) => void
  disabled?: boolean
  /**
   * Apps (`core`/`crm`/`hrm`) the agent has a connected+enabled MCP server for.
   * When provided, a group whose app isn't here is locked: its scopes are inert
   * until the matching tools are connected, so editing is disabled and a hint
   * points to the Connections tab. Omit to disable gating entirely.
   */
  connectedApps?: Set<string>
  /** Called from a gated group's hint to jump to the Connections tab. */
  onConnectApp?: (app: string) => void
}

/**
 * Grouped scope picker: apps (Core / CRM …) → resources (Members, Workspaces …)
 * → per-action checkboxes (Read / Write / Manage …), each action being one
 * `resource:action` scope code. The selection is a flat `Set<string>` of codes;
 * the parent serialises it to the agent's `allowed_scopes` string.
 */
export const ScopePicker = ({
  catalog,
  value,
  onChange,
  disabled,
  connectedApps,
  onConnectApp
}: Props) => {
  const { t } = useTranslation()
  const groups = groupScopes(catalog)

  const toggle = (codes: string[], on: boolean) => {
    const next = new Set(value)
    codes.forEach((code) => (on ? next.add(code) : next.delete(code)))
    onChange(next)
  }

  const appLabel = (app: string) =>
    t(`agents.scopes.app.${app}`, { defaultValue: app.toUpperCase() })
  const actionLabel = (action: string) =>
    t(`agents.scopes.action.${action}`, { defaultValue: action })
  const resourceLabel = (resource: string) =>
    t(`agents.scopes.resource.${resource}`, {
      defaultValue: resource.split(':').slice(1).join(' · ') || resource
    })

  return (
    <Flex
      vertical
      style={{
        border: '1px solid var(--ant-color-border-secondary)',
        borderRadius: 7,
        overflow: 'hidden'
      }}
    >
      {groups.map((group, gi) => {
        const codes = group.resources.flatMap((r) =>
          r.rows.map((row) => row.code)
        )
        const granted = codes.filter((code) => value.has(code)).length
        const allOn = granted === codes.length && codes.length > 0
        const someOn = granted > 0
        // Locked when gating is on and this app has no connected+enabled server.
        const gated = connectedApps ? !connectedApps.has(group.app) : false

        return (
          <div key={group.app}>
            {gi > 0 ? (
              <div
                style={{
                  height: 1,
                  background: 'var(--ant-color-border-secondary)'
                }}
              />
            ) : null}
            <Flex
              justify="space-between"
              align="center"
              style={{
                padding: '8px 12px',
                background: 'var(--ant-color-fill-tertiary)',
                borderBottom: '1px solid var(--ant-color-border-secondary)'
              }}
            >
              <Flex align="center" gap={6}>
                <Checkbox
                  checked={allOn}
                  indeterminate={someOn && !allOn}
                  disabled={disabled || gated}
                  onChange={(e) => toggle(codes, e.target.checked)}
                />
                <SectionLabel>{appLabel(group.app)}</SectionLabel>
              </Flex>
              <Text
                type="secondary"
                style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
              >
                {granted} / {codes.length}
              </Text>
            </Flex>

            {gated ? (
              <Flex
                align="center"
                gap={6}
                wrap="wrap"
                style={{
                  padding: '8px 12px 8px 36px',
                  borderBottom: '1px solid var(--ant-color-fill-tertiary)'
                }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('agents.scopes.gatedHint', {
                    app: appLabel(group.app)
                  })}
                </Text>
                {onConnectApp ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', fontSize: 12 }}
                    onClick={() => onConnectApp(group.app)}
                  >
                    {t('agents.scopes.gatedConnect')}
                    <ArrowRight size={12} style={{ marginInlineStart: 2 }} />
                  </Button>
                ) : null}
              </Flex>
            ) : (
              group.resources.map((res) => (
                <Flex
                  key={res.resource}
                  align="center"
                  justify="space-between"
                  gap={12}
                  style={{
                    padding: '6px 12px 6px 36px',
                    borderBottom: '1px solid var(--ant-color-fill-tertiary)'
                  }}
                >
                  <Text
                    style={{ fontSize: 12.5, fontWeight: 500, minWidth: 0 }}
                  >
                    {resourceLabel(res.resource)}
                  </Text>
                  <Flex gap={16} wrap="wrap">
                    {res.rows.map((row) => (
                      <Checkbox
                        key={row.code}
                        checked={value.has(row.code)}
                        disabled={disabled}
                        onChange={(e) => toggle([row.code], e.target.checked)}
                      >
                        <span
                          title={row.description}
                          style={{ fontSize: 12.5 }}
                        >
                          {actionLabel(row.action)}
                        </span>
                      </Checkbox>
                    ))}
                  </Flex>
                </Flex>
              ))
            )}
          </div>
        )
      })}
    </Flex>
  )
}
